import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, FormGroup, Label, Form, Row, Col, Card, CardBody, InputGroup, InputGroupText } from "reactstrap";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

const AddStaff = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Form States
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        password: ""
    });

    // Password Visibility State
    const [showPassword, setShowPassword] = useState(false);

    // Error States
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (errors[e.target.name]) {
            setErrors({ ...errors, [e.target.name]: "" });
        }
    };

    // 🔥 Frontend Validation Logic
    const validateForm = () => {
        let formErrors = {};
        let isValid = true;

        if (!formData.name.trim()) {
            formErrors.name = "Staff name is required";
            isValid = false;
        }

        if (!formData.email.trim()) {
            formErrors.email = "Email address is required";
            isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            formErrors.email = "Invalid email format";
            isValid = false;
        }

        if (!formData.phone.trim()) {
            formErrors.phone = "Phone number is required";
            isValid = false;
        } else if (formData.phone.length !== 10) {
            formErrors.phone = "Phone number must be 10 digits";
            isValid = false;
        }

        if (!formData.password.trim()) {
            formErrors.password = "Password is required";
            isValid = false;
        } else if (formData.password.length < 6) {
            formErrors.password = "Password must be at least 6 characters";
            isValid = false;
        }

        setErrors(formErrors);
        
        if (!isValid) {
            toast.error("Please fill all required fields correctly!", {
                toastId: "validation-error" // Unique ID so it won't repeat
            });
        }

        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // 1. Check Frontend Validation
        if (!validateForm()) return;

        dispatch(loadingTrue());
        setErrors({});

        try {
            const res = await Handler({ method: "POST", url: "/staff/create.php", data: formData });
            if (res?.success) {
                Swal.fire("Success!", "Staff created successfully", "success");
                navigate("/staffs");
                dispatch(loadingFalse());
            } else {
                if (res?.errors) {
                    setErrors(res.errors);
                    dispatch(loadingFalse());
                } else {
                    toast.error(res?.message || "Something went wrong");
                }
            }
        } catch (err) {
            Swal.fire("Error", "Server error occurred", "error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    return (
        <div className="page-container p-1">
            <div className="page-header mb-4">
                <h6 className="text-muted">Management &gt; Staffs</h6>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="fw-bold">Add New Staff</h3>
                        <p className="text-muted">Fill in the details to create a new staff account.</p>
                    </div>
                    <Button color="secondary" outline onClick={() => navigate(-1)}>
                        <i className="bx bx-arrow-back"></i> Back
                    </Button>
                </div>
            </div>

            <Card className="border-0 shadow-sm" style={{ borderRadius: '15px' }}>
                <CardBody className="p-4">
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="name">Staff Name</Label>
                                    <Input
                                        type="text"
                                        name="name"
                                        id="name"
                                        placeholder="Enter full name"
                                        className={errors.name ? "is-invalid" : ""}
                                        value={formData.name}
                                        onChange={handleChange}
                                    />
                                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="email">Email Address</Label>
                                    <Input
                                        type="email"
                                        name="email"
                                        id="email"
                                        placeholder="staff@example.com"
                                        className={errors.email ? "is-invalid" : ""}
                                        value={formData.email}
                                        onChange={handleChange}
                                    />
                                    {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row className="mt-3">
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="phone">Phone Number</Label>
                                    <Input
                                        type="text"
                                        name="phone"
                                        id="phone"
                                        placeholder="10 digit number"
                                        maxLength="10"
                                        className={errors.phone ? "is-invalid" : ""}
                                        value={formData.phone}
                                        onChange={handleChange}
                                    />
                                    {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="password">Login Password</Label>
                                    <InputGroup className={errors.password ? "is-invalid" : ""}>
                                        <Input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            id="password"
                                            placeholder="Min 6 characters"
                                            className={errors.password ? "is-invalid" : ""}
                                            value={formData.password}
                                            onChange={handleChange}
                                        />
                                        <InputGroupText
                                            onClick={() => setShowPassword(!showPassword)}
                                            style={{ cursor: 'pointer', background: '#f8f9fa' }}
                                        >
                                            <i className={showPassword ? "bx bx-show" : "bx bx-hide"}></i>
                                        </InputGroupText>
                                    </InputGroup>
                                    {errors.password && <div className="invalid-feedback d-block">{errors.password}</div>}
                                </FormGroup>
                            </Col>
                        </Row>

                        <div className="mt-4 pt-3 border-top d-flex justify-content-end">
                            <Button type="reset" color="light" className="me-2" onClick={() => {
                                setFormData({ name: "", email: "", phone: "", password: "" });
                                setErrors({});
                            }}>
                                Clear
                            </Button>
                            <Button type="submit" className="add-btn px-5">
                                <i className="bx bx-check-circle me-1"></i> Submit
                            </Button>
                        </div>
                    </Form>
                </CardBody>
            </Card>
        </div>
    );
};

export default AddStaff;